import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiAlertOutline, mdiClose, mdiPaperclip } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { DocumentPickerDialog, type AttachDocumentControlLabels, type DocLite } from '../documents/AttachDocumentControl'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * A library document picked in the composer to go out with the reply (spec
 * 2026-10-10-05). `internal` is the document's resolved audience: sending one
 * to a resident takes an explicit confirmation, its own visibility never
 * changes.
 */
export interface SharedDocument {
    uid: string
    name: string
    internal: boolean
}

export function toSharedDocument(doc: DocLite): SharedDocument {
    return { uid: doc.uid, name: doc.name, internal: doc.effective_visibility !== 'shared' }
}

/**
 * The composer's "Attach a document" action, the picked documents as chips and
 * the "visible to the resident" hint. The picker lists what the documents API
 * returns, which is already narrowed to what the member may read and never
 * lists a document private to a thread.
 */
export function ComposerDocuments({
    org,
    documents,
    onAdd,
    onRemove,
    disabled,
}: {
    org: string
    documents: SharedDocument[]
    onAdd: (doc: SharedDocument) => void
    onRemove: (uid: string) => void
    disabled?: boolean
}) {
    const { t } = useTranslation('conversations')
    const [open, setOpen] = useState(false)
    const linked = useMemo(() => new Set(documents.map((d) => d.uid)), [documents])
    const labels: AttachDocumentControlLabels = {
        attachExisting: t('share.attach'),
        upload: '',
        uploading: '',
        pickDocumentTitle: t('share.pick_title'),
        searchPlaceholder: t('share.search'),
        noDocumentsFound: t('share.none'),
        alreadyAttached: t('share.already'),
    }

    return (
        <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={disabled}
                    onClick={() => setOpen(true)}
                    data-testid="conversation-share-doc-pick-btn"
                >
                    <AppIcon path={mdiPaperclip} size="xs" className="mr-1" />
                    {t('share.attach')}
                </Button>
                {documents.map((doc) => (
                    <span
                        key={doc.uid}
                        className="inline-flex max-w-[16rem] items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
                        data-testid={`conversation-shared-doc-${doc.uid}`}
                    >
                        <AppIcon path={SECTION_ICONS.documents} size="xs" tone="muted" className="flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        {doc.internal && (
                            <Badge variant="warning" className="px-1 py-0 text-[10px]" data-testid={`conversation-shared-doc-internal-${doc.uid}`}>
                                {t('share.internal')}
                            </Badge>
                        )}
                        <button
                            type="button"
                            className="ml-0.5 rounded text-muted-foreground hover:text-foreground"
                            onClick={() => onRemove(doc.uid)}
                            aria-label={t('share.remove', { name: doc.name })}
                            title={t('share.remove', { name: doc.name })}
                            data-testid={`conversation-shared-doc-remove-${doc.uid}`}
                        >
                            <AppIcon path={mdiClose} size="xs" />
                        </button>
                    </span>
                ))}
            </div>
            {documents.length > 0 && (
                <p className="flex items-start gap-1 text-xs text-muted-foreground" data-testid="conversation-shared-docs-hint">
                    <AppIcon path={mdiAlertOutline} size="xs" className="mt-0.5 flex-shrink-0" aria-hidden />
                    <span>{t('share.visible_to_resident')}</span>
                </p>
            )}
            {open && (
                <DocumentPickerDialog
                    org={org}
                    open={open}
                    onOpenChange={setOpen}
                    linkedUids={linked}
                    testidPrefix="conversation-share-doc"
                    labels={labels}
                    rowHint={(doc) =>
                        doc.effective_visibility && doc.effective_visibility !== 'shared' ? (
                            <Badge variant="warning" className="px-1 py-0 text-[10px]" data-testid={`conversation-share-doc-internal-${doc.uid}`}>
                                {t('share.internal')}
                            </Badge>
                        ) : null
                    }
                    onPick={(_uid, doc) => {
                        onAdd(toSharedDocument(doc))
                        setOpen(false)
                    }}
                />
            )}
        </div>
    )
}

/**
 * The "send an internal document anyway?" confirmation. Opened before a send
 * that carries one, and when the server answers DOCUMENT_INTERNAL.
 */
export function ConfirmInternalDocumentsDialog({
    open,
    names,
    onCancel,
    onConfirm,
}: {
    open: boolean
    names: string[]
    onCancel: () => void
    onConfirm: () => void
}) {
    const { t } = useTranslation('conversations')

    return (
        <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
            <AlertDialogContent data-testid="conversation-share-internal-confirm">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('share.confirm_title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('share.confirm_body', { names: names.join(', ') })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="conversation-share-internal-cancel">{t('share.confirm_cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} data-testid="conversation-share-internal-send">
                        {t('share.confirm_send')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
